import { useState } from "react";

import { Callout, Colors, Icon } from "@blueprintjs/core";
import { Head, useHead } from "@unhead/react";

import metaImg from "@/../../hypetrain/static/img/pro-meta.png";
import { SEOHead } from "@/components/helpers/seo/SEOHead";
import { pluralize } from "@/components/helpers/string-utils";
import { Box, Flex } from "@/components/layout/flexbox";
import { PublicNav } from "@/components/nav/PublicNav";
import { PageContainer } from "@/components/pages_flexible/PageContainer";
import { Container } from "@/components/pieces/bootstrap";
import { Brands } from "@/components/pieces/marketing/Brands";
import { FAQ } from "@/components/pieces/marketing/FAQ";
import { GoldBar } from "@/components/pieces/marketing/GoldBar";
import { ProBenefits } from "@/components/pieces/marketing/ProBenefits";
import { ProQuotes } from "@/components/pieces/marketing/ProQuotes";
import { Testimonials } from "@/components/pieces/marketing/Testimonials";
import { UseCases } from "@/components/pieces/marketing/UseCases";
import { UserExperience } from "@/components/pieces/marketing/UserExperience";
import { RedeemGiftCode } from "@/components/pieces/menu/MenuBilling/RedeemGiftCode";
import { getUser } from "@/components/session/JwtTokenManager";
import {
  BRAND_PURPLE,
  CARD_CLASSES,
  LIGHTEST_BACKGROUND,
  SITE_NAME,
  STANDARD_NAV_HEIGHT,
} from "@/css/constants";
import { RedeemGiftCodeMutation } from "@/graphql";
import { formatCreditsLowerCase } from "@/utils/string";

import { MainHero } from "./MainHero";

function PublicRedeem() {
  return (
    <>
      <PublicNav />
      <Box
        mt={STANDARD_NAV_HEIGHT}
        css={{ backgroundColor: LIGHTEST_BACKGROUND }}
      >
        <MainHero />
        <Brands opacity={1} backgroundColor="#2D2750" />
        <GoldBar />
        <UserExperience />
        <ProBenefits />
        <ProQuotes />
        <FAQ category="Gift codes" />
        <GoldBar />
        <UseCases />
        <Testimonials />
      </Box>
    </>
  );
}

function LoggedInRedeem() {
  const [creditsRedeemed, setCreditsRedeemed] = useState<string>();
  function onCompleted(response: RedeemGiftCodeMutation) {
    if (response.redeemGiftCode?.creditsRedeemed) {
      setCreditsRedeemed(response.redeemGiftCode.creditsRedeemed);
    }
  }

  return (
    <>
      <Box className="bbs" py={4} bg={Colors.WHITE}>
        <Container>
          <Flex alignItems="center">
            <Icon icon="barcode" color={BRAND_PURPLE} size={26} />
            <Box ml={3} fontFamily="poppins">
              <h1 style={{ fontSize: 28, margin: 0 }}>Redeem Gift Code</h1>
            </Box>
          </Flex>
        </Container>
      </Box>
      <Container flex={1} py={[2, 4]}>
        {!creditsRedeemed ? (
          <Box
            flex={[null, null, 1]}
            backgroundColor="#FFF"
            className={CARD_CLASSES}
            p={24}
            maxWidth={["100%", "100%", "100%", 500]}
            mt={24}
          >
            <RedeemGiftCode onCompleted={onCompleted} />
          </Box>
        ) : (
          <Box mt={3}>
            <Callout intent="success" title="🎉 Success!">
              You redeemed {formatCreditsLowerCase(creditsRedeemed)} which{" "}
              {pluralize({
                amount: creditsRedeemed,
                singular: "is",
                plural: "are",
                showCount: false,
              })}
              your account.
            </Callout>
          </Box>
        )}
      </Container>
    </>
  );
}

function AuthRedeem() {
  return getUser() ? <LoggedInRedeem /> : <PublicRedeem />;
}

export function RedeemPage() {
  useHead({
    bodyAttrs: { class: "public-site" },
  });
  return (
    <PageContainer layout="full">
      <SEOHead
        title={`Redeem Gift Code | ${SITE_NAME}`}
        description={`Redeem your ${SITE_NAME} gift code. Unlock your full potential with a coach.`}
        canonicalPath="/redeem"
        image={metaImg}
      />
      <Head>
        <style type="text/css">{`
          #root {
            overflow-x: hidden;
          }
        `}</style>
      </Head>
      <AuthRedeem />
    </PageContainer>
  );
}
